import { useMemo, useCallback, useState, useEffect } from 'react'
import { format } from 'date-fns'
import { useAppSelector } from '../../../../../redux/store'
import { convertUtcToEst } from '../../../../../utils/DateAndTimeFormat'

const FIFTEEN_MINUTES_MS = 15 * 60 * 1000

/**
 * Rounds down a timestamp to the nearest 15-minute interval
 * @param timestamp - The timestamp in milliseconds
 * @returns The rounded down timestamp
 */
const roundDownToNearest15Minutes = (timestamp: number): number => {
  const date = new Date(timestamp)
  const minutes = date.getMinutes()
  const roundedMinutes = minutes - (minutes % 15)

  const roundedDate = new Date(date)
  roundedDate.setMinutes(roundedMinutes)
  roundedDate.setSeconds(0)
  roundedDate.setMilliseconds(0)

  return roundedDate.getTime()
}

const useTraffic = () => {
  const queue = useAppSelector((state) => state.lobbyPortal.queue)
  const pollLobbyQueueStatus = useAppSelector((state) => state.lobbyPortal.pollLobbyQueueStatus)

  // EST timestamp formatter
  const timeStamp = useCallback((time: string) => {
    const stamp = new Date(time)
    const estTime = convertUtcToEst(stamp)
    return format(estTime, 'h:mm a')
  }, [])

  const filteredQueue = useMemo(() => {
    if (!queue) return queue
    const now = Date.now()
    const windowStart = roundDownToNearest15Minutes(now)
    const windowEnd = windowStart + FIFTEEN_MINUTES_MS

    const filteredUpcoming =
      queue.upcoming?.filter((person) => {
        const appointmentTime = person.visit?.appointmentTime
        if (!appointmentTime) return false

        const timestamp = new Date(appointmentTime).getTime()
        if (Number.isNaN(timestamp)) return false

        return timestamp >= windowStart && timestamp <= windowEnd
      }) ?? []

    // Add timestamp flags to upcoming appointments
    const addTimestampFlag = (list: any[]) => {
      return list.map((item, index) => {
        const fieldValue = item.visit?.appointmentTime
        const current = fieldValue ? timeStamp(fieldValue) : '-'
        const prevFieldValue = index > 0 && list[index - 1] ? list[index - 1].visit?.appointmentTime : null
        const previous = prevFieldValue ? timeStamp(prevFieldValue) : null

        return {
          ...item,
          showTimeStamp: current !== previous,
          timeStamp: current,
        }
      })
    }

    const upcomingWithTimestamps = addTimestampFlag(filteredUpcoming)

    return {
      ...queue,
      upcoming: upcomingWithTimestamps,
    }
  }, [queue, timeStamp, pollLobbyQueueStatus])

  return {
    queue: filteredQueue,
  }
}

export default useTraffic
